import React from 'react';
import { contains } from '../utils/contains';
import { setRef } from '../utils/set-ref';
import { useCallbackRef } from './useCallbackRef';
import { useControlled } from './use-controlled';
import { useId } from './use-id';
import { usePreviousProps } from './use-previous-props';

export type AutocompleteFreeSoloValueMapping<FreeSolo> = FreeSolo extends true ? string : never;

export type AutocompleteValueOrFreeSoloValueMapping<Value, FreeSolo> = FreeSolo extends true
  ? Value | string
  : Value;

export type AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo> = Multiple extends true
  ? Array<Value | AutocompleteFreeSoloValueMapping<FreeSolo>>
  : DisableClearable extends true
    ? NonNullable<Value | AutocompleteFreeSoloValueMapping<FreeSolo>>
    : Value | null | AutocompleteFreeSoloValueMapping<FreeSolo>;

export type AutocompleteHighlightChangeReason = 'keyboard' | 'mouse' | 'touch';

export type AutocompleteChangeReason =
  | 'createOption'
  | 'selectOption'
  | 'removeOption'
  | 'clear'
  | 'blur';

export interface AutocompleteChangeDetails<Value = string> {
  option: Value;
}

export type AutocompleteCloseReason =
  | 'createOption'
  | 'toggleInput'
  | 'escape'
  | 'selectOption'
  | 'removeOption'
  | 'blur';

export type AutocompleteInputChangeReason =
  | 'input'
  | 'reset'
  | 'clear'
  | 'blur'
  | 'selectOption'
  | 'removeOption';

export interface AutocompleteGroupedOption<Value = string> {
  key: number;
  index: number;
  group: string;
  options: Array<Value>;
}

export interface FilterOptionsState<Value> {
  inputValue: string;
  getOptionLabel: (option: Value) => string;
}

export interface UseAutocompleteProps<
  Value,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
> {
  autoComplete?: boolean;
  autoHighlight?: boolean;
  autoSelect?: boolean;
  blurOnSelect?: 'touch' | 'mouse' | true | false;
  clearOnBlur?: boolean;
  clearOnEscape?: boolean;
  componentName?: string;
  defaultValue?: AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>;
  disableClearable?: DisableClearable;
  disableCloseOnSelect?: boolean;
  disabled?: boolean;
  disabledItemsFocusable?: boolean;
  disableListWrap?: boolean;
  filterOptions?: (options: Array<Value>, state: FilterOptionsState<Value>) => Array<Value>;
  filterSelectedOptions?: boolean;
  freeSolo?: FreeSolo;
  getOptionDisabled?: (option: Value) => boolean;
  getOptionKey?: (
    option: Value | AutocompleteFreeSoloValueMapping<FreeSolo>
  ) => string | number;
  getOptionLabel?: (option: AutocompleteValueOrFreeSoloValueMapping<Value, FreeSolo>) => string;
  groupBy?: (option: Value) => string;
  handleHomeEndKeys?: boolean;
  id?: string;
  includeInputInList?: boolean;
  inputValue?: string;
  isOptionEqualToValue?: (
    option: Value,
    value: AutocompleteValueOrFreeSoloValueMapping<Value, FreeSolo>
  ) => boolean;
  multiple?: Multiple;
  onChange?: (
    event: React.SyntheticEvent,
    value: AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<Value>
  ) => void;
  onClose?: (event: React.SyntheticEvent, reason: AutocompleteCloseReason) => void;
  onHighlightChange?: (
    event: React.SyntheticEvent,
    option: Value | null,
    reason: AutocompleteHighlightChangeReason
  ) => void;
  onInputChange?: (
    event: React.SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => void;
  onOpen?: (event: React.SyntheticEvent) => void;
  open?: boolean;
  openOnFocus?: boolean;
  options: ReadonlyArray<Value>;
  readOnly?: boolean;
  /**
   * Internal: when defined, marks single-value rendering as a chip-like tag
   * (Backspace/Delete remove it instead of clearing the input).
   */
  renderValue?: unknown;
  selectOnFocus?: boolean;
  value?: AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>;
}

export interface AutocompleteRootProps extends React.HTMLAttributes<HTMLDivElement> {
  onMouseDown: React.MouseEventHandler<HTMLDivElement>;
  onClick: React.MouseEventHandler<HTMLDivElement>;
  onKeyDown: React.KeyboardEventHandler<HTMLDivElement>;
}

export interface AutocompleteInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'ref'> {
  ref: React.Ref<HTMLInputElement>;
}

export interface AutocompleteOptionProps extends React.HTMLAttributes<HTMLLIElement> {
  key: React.Key;
  'data-option-index': number;
  'aria-disabled': boolean;
  'aria-selected': boolean;
}

export interface AutocompleteListboxProps extends React.HTMLAttributes<HTMLUListElement> {
  ref: React.Ref<HTMLUListElement>;
}

export interface AutocompleteItemProps {
  key?: number;
  'data-item-index': number;
  tabIndex: -1;
  onDelete: (event: React.SyntheticEvent) => void;
}

export type AutocompleteGetItemProps<Multiple extends boolean | undefined> = Multiple extends true
  ? (args: { index: number }) => AutocompleteItemProps & { key: number }
  : (args?: { index?: number }) => AutocompleteItemProps;

export interface UseAutocompleteRenderedOption<Value> {
  option: Value;
  index: number;
}

export interface UseAutocompleteReturnValue<
  Value,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
  HasGroupBy extends boolean = false,
> {
  getRootProps: (externalProps?: Partial<AutocompleteRootProps>) => AutocompleteRootProps;
  getInputProps: () => AutocompleteInputProps;
  getInputLabelProps: () => Omit<React.HTMLAttributes<HTMLLabelElement>, 'color'> & {
    htmlFor: string;
    id: string;
  };
  getClearProps: () => React.ButtonHTMLAttributes<HTMLButtonElement>;
  getItemProps: AutocompleteGetItemProps<Multiple>;
  getPopupIndicatorProps: () => React.ButtonHTMLAttributes<HTMLButtonElement>;
  getListboxProps: () => AutocompleteListboxProps;
  getOptionProps: (
    renderedOption: UseAutocompleteRenderedOption<Value>
  ) => AutocompleteOptionProps;
  id: string;
  inputValue: string;
  value: AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>;
  dirty: boolean;
  expanded: boolean;
  popupOpen: boolean;
  focused: boolean;
  anchorEl: HTMLElement | null;
  setAnchorEl: (node: HTMLElement | null) => void;
  focusedItem: number;
  groupedOptions: HasGroupBy extends true
    ? Array<AutocompleteGroupedOption<Value>>
    : Array<Value>;
}

const PageSize = 5;

const MultipleDefaultValue: ReadonlyArray<unknown> = [];

const defaultIsActiveElementInListbox = (
  listboxRef: React.RefObject<HTMLElement | null>
): boolean =>
  listboxRef.current !== null &&
  contains(listboxRef.current.parentElement, document.activeElement);

const defaultIsOptionEqualToValue = (option: unknown, value: unknown): boolean => option === value;

interface AreArraysSameArgs<T> {
  array1: ReadonlyArray<T> | undefined | null;
  array2: ReadonlyArray<T> | undefined | null;
  parser?: (value: T) => unknown;
}

const areArraysSame = <T,>({ array1, array2, parser = (value) => value }: AreArraysSameArgs<T>): boolean => {
  if (!array1 || !array2 || array1.length !== array2.length) {
    return false;
  }
  return array1.every((item, index) => parser(item) === parser(array2[index] as T));
};

const getInputValue = <Value,>(
  value: Value | Array<Value> | null | undefined,
  multiple: boolean,
  getOptionLabel: (option: Value) => string,
  hasRenderValue?: boolean
): string => {
  if (multiple || value == null || hasRenderValue === true) {
    return '';
  }
  const optionLabel = getOptionLabel(value as Value);
  return typeof optionLabel === 'string' ? optionLabel : '';
};

interface ChangeHighlightedIndexArgs {
  event?: React.SyntheticEvent;
  diff: number | 'reset' | 'start' | 'end';
  direction?: 'next' | 'previous';
  reason?: AutocompleteHighlightChangeReason;
  preserveScroll?: boolean;
}

interface SetHighlightedIndexArgs {
  event?: React.SyntheticEvent;
  index: number;
  reason?: AutocompleteHighlightChangeReason | null;
  preserveScroll?: boolean;
}

interface SyncToDOMArgs {
  index: number;
  reason?: AutocompleteHighlightChangeReason | null;
  preserveScroll?: boolean;
}

/**
 * Headless logic for combobox-style autocomplete inputs.
 *
 * Returns prop getters and state needed by an `Autocomplete`-style component.
 *
 * Inspired by [material-ui/use-autocomplete](https://mui.com/material-ui/react-autocomplete/#useautocomplete).
 *
 * @returns Prop getters (`getRootProps`, `getInputProps`, `getListboxProps`, etc.)
 * along with derived state including `value`, `inputValue`, `popupOpen`, `groupedOptions`, etc.
 */
export const useAutocomplete = <
  Value,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>(
  props: UseAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>
): UseAutocompleteReturnValue<Value, Multiple, DisableClearable, FreeSolo, boolean> => {
  const {
    autoComplete = false,
    autoHighlight = false,
    autoSelect = false,
    blurOnSelect = false,
    clearOnBlur = !props.freeSolo,
    clearOnEscape = false,
    componentName = 'useAutocomplete',
    defaultValue = (props.multiple ? (MultipleDefaultValue as unknown) : null) as AutocompleteValue<
      Value,
      Multiple,
      DisableClearable,
      FreeSolo
    >,
    disableClearable = false,
    disableCloseOnSelect = false,
    disabled: disabledProp = false,
    disabledItemsFocusable = false,
    disableListWrap = false,
    filterOptions = defaultFilterOptions as unknown as (
      options: Array<Value>,
      state: FilterOptionsState<Value>
    ) => Array<Value>,
    filterSelectedOptions = false,
    freeSolo = false,
    getOptionDisabled,
    getOptionKey,
    getOptionLabel: getOptionLabelProp,
    groupBy,
    handleHomeEndKeys = !props.freeSolo,
    id: idProp,
    includeInputInList = false,
    inputValue: inputValueProp,
    isOptionEqualToValue = defaultIsOptionEqualToValue as (
      option: Value,
      value: AutocompleteValueOrFreeSoloValueMapping<Value, FreeSolo>
    ) => boolean,
    multiple = false as Multiple,
    onChange,
    onClose,
    onHighlightChange,
    onInputChange,
    onOpen,
    open: openProp,
    openOnFocus = false,
    options,
    readOnly = false,
    renderValue,
    selectOnFocus = !props.freeSolo,
    value: valueProp,
  } = props;

  const id = useId(idProp);

  const getOptionLabel = React.useCallback(
    (option: Value | string): string => {
      const labelGetter =
        getOptionLabelProp ??
        (((opt: Value | string) =>
          (opt as { label?: string })?.label ?? (opt as unknown as string)) as (
          option: AutocompleteValueOrFreeSoloValueMapping<Value, FreeSolo>
        ) => string);
      const optionLabel = labelGetter(option as AutocompleteValueOrFreeSoloValueMapping<Value, FreeSolo>);
      if (typeof optionLabel !== 'string') {
        return String(optionLabel);
      }
      return optionLabel;
    },
    [getOptionLabelProp]
  );

  const ignoreFocus = React.useRef(false);
  const firstFocus = React.useRef(true);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const listboxRef = React.useRef<HTMLElement | null>(null);
  const windowLostFocus = React.useRef(false);
  const [anchorEl, setAnchorElState] = React.useState<HTMLElement | null>(null);

  const [focusedItem, setFocusedItem] = React.useState(-1);
  const defaultHighlighted = autoHighlight ? 0 : -1;
  const highlightedIndexRef = React.useRef(defaultHighlighted);

  const highlightReasonRef = React.useRef<AutocompleteHighlightChangeReason | null>(null);

  const touchScrolledRef = React.useRef(false);
  const isTouchRef = React.useRef(false);

  const initialInputValue = React.useRef(
    getInputValue<Value>(
      ((defaultValue as unknown) ?? valueProp) as Value | Array<Value> | null,
      Boolean(multiple),
      getOptionLabel as (option: Value) => string
    )
  ).current;

  const [value, setValueState] = useControlled<AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>>({
    controlled: valueProp,
    default: defaultValue,
    name: componentName,
  });

  const [inputValue, setInputValueState] = useControlled<string>({
    controlled: inputValueProp,
    default: initialInputValue,
    name: componentName,
    state: 'inputValue',
  });

  const [focused, setFocused] = React.useState(false);

  const valueAsArray = React.useMemo<Array<Value>>(() => {
    if (multiple) {
      return (value as unknown as Array<Value>) ?? [];
    }
    if (value != null) {
      return [value as unknown as Value];
    }
    return [];
  }, [multiple, value]);

  const resetInputValue = useCallbackRef(
    (
      event: React.SyntheticEvent | null,
      newValue: AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>,
      reason: AutocompleteInputChangeReason
    ) => {
      const newValueLength = multiple ? (newValue as unknown as Array<Value>).length : newValue !== null ? 1 : 0;
      const previousLength = multiple ? (value as unknown as Array<Value>).length : 0;
      const isOptionSelected = multiple ? previousLength < newValueLength : newValue !== null;
      const shouldClearOnReset = reason === 'reset' && freeSolo && !multiple && newValue === null;

      if (!isOptionSelected && !clearOnBlur && !shouldClearOnReset) {
        return;
      }

      const newInputValue = getInputValue<Value>(
        newValue as unknown as Value | Array<Value> | null,
        Boolean(multiple),
        getOptionLabel as (option: Value) => string,
        renderValue !== undefined
      );

      if (inputValue === newInputValue) {
        return;
      }

      setInputValueState(newInputValue);

      if (onInputChange && event) {
        onInputChange(event, newInputValue, reason);
      }
    }
  );

  const [open, setOpenState] = useControlled<boolean>({
    controlled: openProp,
    default: false,
    name: componentName,
    state: 'open',
  });

  const [inputPristine, setInputPristine] = React.useState(true);

  const inputValueIsSelectedValue =
    !multiple && value != null && inputValue === getOptionLabel(value as unknown as Value);

  const popupOpen = open && !readOnly;

  const selectedValuesSet = React.useMemo<Set<Value> | null>(() => {
    if (
      isOptionEqualToValue !== (defaultIsOptionEqualToValue as typeof isOptionEqualToValue) ||
      valueAsArray.length === 0
    ) {
      return null;
    }
    return new Set(valueAsArray);
  }, [isOptionEqualToValue, valueAsArray]);

  const isOptionSelected = React.useCallback(
    (option: Value): boolean => {
      if (selectedValuesSet) {
        return selectedValuesSet.has(option);
      }
      return valueAsArray.some(
        (selected) =>
          selected != null &&
          isOptionEqualToValue(option, selected as AutocompleteValueOrFreeSoloValueMapping<Value, FreeSolo>)
      );
    },
    [isOptionEqualToValue, valueAsArray, selectedValuesSet]
  );

  const filteredOptions = popupOpen
    ? filterOptions(
        (options as Array<Value>).filter((option) => {
          if (filterSelectedOptions && isOptionSelected(option)) {
            return false;
          }
          return true;
        }),
        {
          inputValue: inputValueIsSelectedValue && inputPristine ? '' : inputValue,
          getOptionLabel: getOptionLabel as (option: Value) => string,
        }
      )
    : [];

  const previousProps = usePreviousProps({
    filteredOptions,
    value,
    inputValue,
  });

  React.useEffect(() => {
    const valueChange = value !== previousProps.value;

    if (focused && !valueChange) {
      return;
    }

    if (freeSolo && !valueChange) {
      return;
    }

    resetInputValue(null, value, 'reset');
  }, [value, resetInputValue, focused, previousProps.value, freeSolo]);

  const listboxAvailable = open && filteredOptions.length > 0 && !readOnly;

  const focusItem = useCallbackRef((itemToFocus: number) => {
    if (itemToFocus === -1) {
      inputRef.current?.focus();
    } else {
      const target = anchorEl?.querySelector<HTMLElement>(`[data-item-index="${itemToFocus}"]`);
      target?.focus();
    }
  });

  React.useEffect(() => {
    if (multiple && focusedItem > valueAsArray.length - 1) {
      setFocusedItem(-1);
      focusItem(-1);
    }
  }, [valueAsArray, multiple, focusedItem, focusItem]);

  const validOptionIndex = (index: number, direction: 'next' | 'previous'): number => {
    if (!listboxRef.current || index < 0 || index >= filteredOptions.length) {
      return -1;
    }
    let nextFocus = index;
    while (true) {
      const option = listboxRef.current.querySelector<HTMLElement>(
        `[data-option-index="${nextFocus}"]`
      );
      const nextFocusDisabled = disabledItemsFocusable
        ? false
        : !option ||
          (option as HTMLButtonElement).disabled ||
          option.getAttribute('aria-disabled') === 'true';
      if (option && option.hasAttribute('tabindex') && !nextFocusDisabled) {
        return nextFocus;
      }
      if (direction === 'next') {
        nextFocus = (nextFocus + 1) % filteredOptions.length;
      } else {
        nextFocus = (nextFocus - 1 + filteredOptions.length) % filteredOptions.length;
      }
      if (nextFocus === index) {
        return -1;
      }
    }
  };

  const syncHighlightedIndexToDOM = useCallbackRef(
    ({ index, reason, preserveScroll = false }: SyncToDOMArgs) => {
      if (index === -1) {
        inputRef.current?.removeAttribute('aria-activedescendant');
      } else {
        inputRef.current?.setAttribute('aria-activedescendant', `${id}-option-${index}`);
      }

      if (!listboxRef.current) {
        return;
      }

      const previous = listboxRef.current.querySelector<HTMLElement>(
        `[role="option"].nucleus-focused`
      );
      if (previous) {
        previous.classList.remove('nucleus-focused');
        previous.classList.remove('nucleus-focusVisible');
      }

      let listboxNode: HTMLElement | null = listboxRef.current;
      if (listboxRef.current.getAttribute('role') !== 'listbox') {
        listboxNode = listboxRef.current.parentElement?.querySelector<HTMLElement>(
          '[role="listbox"]'
        ) ?? null;
      }

      if (!listboxNode) {
        return;
      }

      if (index === -1) {
        if (!preserveScroll) {
          listboxNode.scrollTop = 0;
        }
        return;
      }

      const option = listboxRef.current.querySelector<HTMLElement>(
        `[data-option-index="${index}"]`
      );

      if (!option) {
        return;
      }

      option.classList.add('nucleus-focused');
      if (reason === 'keyboard') {
        option.classList.add('nucleus-focusVisible');
      }

      if (
        listboxNode.scrollHeight > listboxNode.clientHeight &&
        reason !== 'mouse' &&
        reason !== 'touch'
      ) {
        const scrollBottom = listboxNode.clientHeight + listboxNode.scrollTop;
        const elementBottom = option.offsetTop + option.offsetHeight;
        if (elementBottom > scrollBottom) {
          listboxNode.scrollTop = elementBottom - listboxNode.clientHeight;
        } else if (
          option.offsetTop - option.offsetHeight * (groupBy ? 1.3 : 0) <
          listboxNode.scrollTop
        ) {
          listboxNode.scrollTop = option.offsetTop - option.offsetHeight * (groupBy ? 1.3 : 0);
        }
      }
    }
  );

  const setHighlightedIndex = useCallbackRef(
    ({ event, index, reason, preserveScroll = false }: SetHighlightedIndexArgs) => {
      highlightedIndexRef.current = index;
      highlightReasonRef.current = reason ?? null;

      if (
        onHighlightChange &&
        event &&
        (reason === 'mouse' || reason === 'keyboard' || reason === 'touch')
      ) {
        onHighlightChange(event, index === -1 ? null : (filteredOptions[index] as Value), reason);
      }

      syncHighlightedIndexToDOM({ index, reason, preserveScroll });
    }
  );

  const setHighlightedIndexFromSync = useCallbackRef(({ index }: { index: number }) => {
    highlightedIndexRef.current = index;
    syncHighlightedIndexToDOM({
      index,
      reason: highlightReasonRef.current,
    });
  });

  const changeHighlightedIndex = useCallbackRef(
    ({ event, diff, direction = 'next', reason, preserveScroll }: ChangeHighlightedIndexArgs) => {
      if (!popupOpen) {
        return;
      }

      if (reason === 'keyboard') {
        touchScrolledRef.current = false;
        isTouchRef.current = false;
      }

      const getNextIndex = (): number => {
        const maxIndex = filteredOptions.length - 1;

        if (diff === 'reset') {
          return defaultHighlighted;
        }
        if (diff === 'start') {
          return 0;
        }
        if (diff === 'end') {
          return maxIndex;
        }

        const numericDiff = diff;
        const newIndex = highlightedIndexRef.current + numericDiff;

        if (newIndex < 0) {
          if (newIndex === -1 && includeInputInList) {
            return -1;
          }
          if ((disableListWrap && highlightedIndexRef.current !== -1) || Math.abs(numericDiff) > 1) {
            return 0;
          }
          return maxIndex;
        }

        if (newIndex > maxIndex) {
          if (newIndex === maxIndex + 1 && includeInputInList) {
            return -1;
          }
          if (disableListWrap || Math.abs(numericDiff) > 1) {
            return maxIndex;
          }
          return 0;
        }

        return newIndex;
      };

      const nextIndex = validOptionIndex(getNextIndex(), direction);
      setHighlightedIndex({ index: nextIndex, reason, event, preserveScroll });

      if (autoComplete && diff !== 'reset' && inputRef.current) {
        if (nextIndex === -1) {
          inputRef.current.value = inputValue;
        } else {
          const option = getOptionLabel(filteredOptions[nextIndex] as Value);
          inputRef.current.value = option;
          const matchIndex = option.toLowerCase().indexOf(inputValue.toLowerCase());
          if (matchIndex === 0 && inputValue.length > 0) {
            inputRef.current.setSelectionRange(inputValue.length, option.length);
          }
        }
      }
    }
  );

  const filteredOptionsChanged = !areArraysSame({
    array1: previousProps.filteredOptions,
    array2: filteredOptions,
    parser: getOptionLabel as (value: Value) => unknown,
  });

  const getPreviousHighlightedOptionIndex = (): number => {
    const isSameLabel = (value1: Value | null | undefined, value2: Value | null | undefined) => {
      const label1 = value1 ? getOptionLabel(value1) : '';
      const label2 = value2 ? getOptionLabel(value2) : '';
      return label1 === label2;
    };

    const previousFiltered = previousProps.filteredOptions;
    const previousValue = previousProps.value;

    if (
      highlightedIndexRef.current !== -1 &&
      previousFiltered &&
      !areArraysSame({
        array1: previousFiltered,
        array2: filteredOptions,
        parser: getOptionLabel as (value: Value) => unknown,
      }) &&
      previousProps.inputValue === inputValue &&
      (multiple
        ? (value as unknown as Array<Value>).length === (previousValue as unknown as Array<Value>)?.length &&
          ((previousValue as unknown as Array<Value>) ?? []).every(
            (val, i) =>
              getOptionLabel((value as unknown as Array<Value>)[i] as Value) === getOptionLabel(val)
          )
        : isSameLabel(previousValue as unknown as Value | null, value as unknown as Value | null))
    ) {
      const previousHighlightedOption = previousFiltered[highlightedIndexRef.current];
      if (previousHighlightedOption) {
        return filteredOptions.findIndex(
          (option) =>
            getOptionLabel(option as Value) === getOptionLabel(previousHighlightedOption as Value)
        );
      }
    }
    return -1;
  };

  const syncHighlightedIndex = React.useCallback(() => {
    if (!popupOpen) {
      return;
    }

    const previousHighlightedOptionIndex = getPreviousHighlightedOptionIndex();
    if (previousHighlightedOptionIndex !== -1) {
      setHighlightedIndexFromSync({ index: previousHighlightedOptionIndex });
      return;
    }

    const valueItem = multiple
      ? (value as unknown as Array<Value>)[0]
      : (value as unknown as Value | null);

    if (filteredOptions.length === 0 || valueItem == null) {
      const previousFiltered = previousProps.filteredOptions;
      const isAppendOnly =
        filteredOptionsChanged &&
        previousProps.inputValue === inputValue &&
        previousFiltered != null &&
        previousFiltered.length > 0 &&
        filteredOptions.length > previousFiltered.length &&
        previousFiltered.every(
          (option, index) =>
            getOptionLabel(option as Value) === getOptionLabel(filteredOptions[index] as Value)
        );

      changeHighlightedIndex({ diff: 'reset', preserveScroll: isAppendOnly });
      return;
    }

    if (!listboxRef.current) {
      return;
    }

    if (valueItem != null) {
      const currentOption = filteredOptions[highlightedIndexRef.current] as Value | undefined;
      if (
        multiple &&
        currentOption &&
        (value as unknown as Array<Value>).findIndex((val) =>
          isOptionEqualToValue(
            currentOption,
            val as AutocompleteValueOrFreeSoloValueMapping<Value, FreeSolo>
          )
        ) !== -1 &&
        previousProps.filteredOptions != null &&
        previousProps.filteredOptions.length > 0
      ) {
        setHighlightedIndexFromSync({ index: highlightedIndexRef.current });
        return;
      }

      const itemIndex = filteredOptions.findIndex((optionItem) =>
        isOptionEqualToValue(
          optionItem as Value,
          valueItem as AutocompleteValueOrFreeSoloValueMapping<Value, FreeSolo>
        )
      );
      if (itemIndex === -1) {
        changeHighlightedIndex({ diff: 'reset' });
      } else {
        setHighlightedIndex({ index: itemIndex });
      }
      return;
    }

    if (highlightedIndexRef.current >= filteredOptions.length - 1) {
      setHighlightedIndex({ index: filteredOptions.length - 1 });
      return;
    }

    setHighlightedIndex({ index: highlightedIndexRef.current });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filteredOptions.length,
    multiple ? false : value,
    changeHighlightedIndex,
    setHighlightedIndex,
    setHighlightedIndexFromSync,
    popupOpen,
    inputValue,
    multiple,
  ]);

  const handleListboxRef = useCallbackRef((node: HTMLElement | null) => {
    setRef(listboxRef, node);
    if (!node) {
      return;
    }
    syncHighlightedIndex();
  });

  React.useEffect(() => {
    if (filteredOptionsChanged || (popupOpen && !disableCloseOnSelect)) {
      syncHighlightedIndex();
    }
  }, [syncHighlightedIndex, filteredOptionsChanged, popupOpen, disableCloseOnSelect]);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const handleWindowBlur = () => {
      windowLostFocus.current = true;
    };
    window.addEventListener('blur', handleWindowBlur);
    return () => {
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, []);

  const handleOpen = (event: React.SyntheticEvent) => {
    if (open) {
      return;
    }
    setOpenState(true);
    setInputPristine(true);
    isTouchRef.current = false;
    if (onOpen) {
      onOpen(event);
    }
  };

  const handleClose = (event: React.SyntheticEvent, reason: AutocompleteCloseReason) => {
    if (!open) {
      return;
    }
    setOpenState(false);
    touchScrolledRef.current = false;
    highlightReasonRef.current = null;
    if (onClose) {
      onClose(event, reason);
    }
  };

  const handleValue = (
    event: React.SyntheticEvent,
    newValue: AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<Value>
  ) => {
    if (multiple) {
      const previousArr = value as unknown as Array<Value>;
      const nextArr = newValue as unknown as Array<Value>;
      if (previousArr.length === nextArr.length && previousArr.every((val, i) => val === nextArr[i])) {
        return;
      }
    } else if (value === newValue) {
      return;
    }

    if (onChange) {
      onChange(event, newValue, reason, details);
    }
    setValueState(newValue);
  };

  const selectNewValue = (
    event: React.SyntheticEvent,
    option: Value | string,
    reasonProp: AutocompleteChangeReason = 'selectOption',
    origin: 'options' | 'freeSolo' = 'options'
  ) => {
    let reason: AutocompleteChangeReason = reasonProp;
    let newValue: unknown = option;

    if (multiple) {
      const baseArr = Array.isArray(value) ? (value as unknown as Array<Value>).slice() : [];
      const itemIndex = baseArr.findIndex((valueItem) =>
        isOptionEqualToValue(
          option as Value,
          valueItem as AutocompleteValueOrFreeSoloValueMapping<Value, FreeSolo>
        )
      );
      if (itemIndex === -1) {
        baseArr.push(option as Value);
      } else if (origin !== 'freeSolo') {
        baseArr.splice(itemIndex, 1);
        reason = 'removeOption';
      }
      newValue = baseArr;
    }

    const finalValue = newValue as AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>;

    resetInputValue(event, finalValue, reason as AutocompleteInputChangeReason);

    handleValue(event, finalValue, reason, { option: option as Value });

    const nativeEvent = (event as unknown as { ctrlKey?: boolean; metaKey?: boolean }) ?? {};
    if (!disableCloseOnSelect && (!event || (!nativeEvent.ctrlKey && !nativeEvent.metaKey))) {
      handleClose(event, reason as AutocompleteCloseReason);
    }

    if (
      blurOnSelect === true ||
      (blurOnSelect === 'touch' && isTouchRef.current) ||
      (blurOnSelect === 'mouse' && !isTouchRef.current)
    ) {
      inputRef.current?.blur();
    }
  };

  const validItemIndex = (index: number, direction: 'next' | 'previous'): number => {
    if (index === -1) {
      return -1;
    }
    let nextFocus = index;
    while (true) {
      if (
        (direction === 'next' && nextFocus === valueAsArray.length) ||
        (direction === 'previous' && nextFocus === -1)
      ) {
        return -1;
      }
      const option = anchorEl?.querySelector<HTMLElement>(`[data-item-index="${nextFocus}"]`);
      if (
        !option ||
        !option.hasAttribute('tabindex') ||
        (option as HTMLButtonElement).disabled ||
        option.getAttribute('aria-disabled') === 'true'
      ) {
        nextFocus += direction === 'next' ? 1 : -1;
      } else {
        return nextFocus;
      }
    }
  };

  const handleFocusItem = (event: React.SyntheticEvent, direction: 'next' | 'previous') => {
    if (!multiple) {
      return;
    }
    if (inputValue === '') {
      handleClose(event, 'toggleInput');
    }

    let nextItem = focusedItem;

    if (focusedItem === -1 && direction === 'previous') {
      nextItem = valueAsArray.length - 1;
      if (freeSolo && inputValue !== '') {
        setInputValueState('');
        if (onInputChange) {
          onInputChange(event, '', 'reset');
        }
      }
    } else {
      nextItem += direction === 'next' ? 1 : -1;
      if (nextItem < 0) {
        nextItem = 0;
      }
      if (nextItem === valueAsArray.length) {
        nextItem = -1;
      }
    }

    nextItem = validItemIndex(nextItem, direction);
    setFocusedItem(nextItem);
    focusItem(nextItem);
  };

  const handleClear = (event: React.SyntheticEvent) => {
    setInputValueState('');
    if (onInputChange) {
      onInputChange(event, '', 'clear');
    }
    handleValue(
      event,
      (multiple ? [] : null) as AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>,
      'clear'
    );
  };

  const handleKeyDown =
    (other: { onKeyDown?: React.KeyboardEventHandler<HTMLDivElement> }) =>
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (other.onKeyDown) {
        other.onKeyDown(event);
      }
      if ((event as unknown as { defaultMuiPrevented?: boolean }).defaultMuiPrevented) {
        return;
      }

      if (focusedItem !== -1 && !['ArrowLeft', 'ArrowRight'].includes(event.key)) {
        setFocusedItem(-1);
        focusItem(-1);
      }

      const nativeEvent = event as unknown as { which?: number };
      if (nativeEvent.which !== 229) {
        switch (event.key) {
          case 'Home':
            if (popupOpen && handleHomeEndKeys) {
              event.preventDefault();
              changeHighlightedIndex({ diff: 'start', direction: 'next', reason: 'keyboard', event });
            }
            break;
          case 'End':
            if (popupOpen && handleHomeEndKeys) {
              event.preventDefault();
              changeHighlightedIndex({ diff: 'end', direction: 'previous', reason: 'keyboard', event });
            }
            break;
          case 'PageUp':
            event.preventDefault();
            changeHighlightedIndex({
              diff: -PageSize,
              direction: 'previous',
              reason: 'keyboard',
              event,
            });
            handleOpen(event);
            break;
          case 'PageDown':
            event.preventDefault();
            changeHighlightedIndex({ diff: PageSize, direction: 'next', reason: 'keyboard', event });
            handleOpen(event);
            break;
          case 'ArrowDown':
            event.preventDefault();
            changeHighlightedIndex({ diff: 1, direction: 'next', reason: 'keyboard', event });
            handleOpen(event);
            break;
          case 'ArrowUp':
            event.preventDefault();
            changeHighlightedIndex({ diff: -1, direction: 'previous', reason: 'keyboard', event });
            handleOpen(event);
            break;
          case 'ArrowLeft': {
            const input = inputRef.current;
            const caretAtStart = input && input.selectionStart === 0 && input.selectionEnd === 0;
            if (!caretAtStart) {
              return;
            }
            if (!multiple && renderValue !== undefined && value != null) {
              if (freeSolo && inputValue !== '') {
                setInputValueState('');
                if (onInputChange) {
                  onInputChange(event, '', 'reset');
                }
              }
              setFocusedItem(0);
              focusItem(0);
            } else {
              handleFocusItem(event, 'previous');
            }
            break;
          }
          case 'ArrowRight':
            if (!multiple && renderValue !== undefined) {
              setFocusedItem(-1);
              focusItem(-1);
            } else {
              handleFocusItem(event, 'next');
            }
            break;
          case 'Enter': {
            const shouldSelectHighlighted =
              !freeSolo || inputPristine || highlightReasonRef.current !== null;

            if (
              highlightedIndexRef.current !== -1 &&
              popupOpen &&
              shouldSelectHighlighted &&
              !touchScrolledRef.current
            ) {
              const option = filteredOptions[highlightedIndexRef.current] as Value;
              const disabled = getOptionDisabled ? getOptionDisabled(option) : false;
              event.preventDefault();
              if (disabled) {
                return;
              }
              selectNewValue(event, option, 'selectOption');
              if (autoComplete && inputRef.current) {
                inputRef.current.setSelectionRange(
                  inputRef.current.value.length,
                  inputRef.current.value.length
                );
              }
            } else if (freeSolo && inputValue !== '' && inputValueIsSelectedValue === false) {
              if (multiple) {
                event.preventDefault();
              }
              selectNewValue(event, inputValue, 'createOption', 'freeSolo');
            } else if (popupOpen && touchScrolledRef.current) {
              event.preventDefault();
              handleClose(event, 'escape');
            }
            break;
          }
          case 'Escape':
            if (popupOpen) {
              event.preventDefault();
              event.stopPropagation();
              handleClose(event, 'escape');
            } else if (
              clearOnEscape &&
              (inputValue !== '' ||
                (multiple && (value as unknown as Array<Value>).length > 0) ||
                renderValue !== undefined)
            ) {
              event.preventDefault();
              event.stopPropagation();
              handleClear(event);
            }
            break;
          case 'Backspace':
            if (
              multiple &&
              !readOnly &&
              inputValue === '' &&
              (value as unknown as Array<Value>).length > 0
            ) {
              const valArr = (value as unknown as Array<Value>).slice();
              const index = focusedItem === -1 ? valArr.length - 1 : focusedItem;
              const removed = valArr[index] as Value;
              valArr.splice(index, 1);
              handleValue(
                event,
                valArr as unknown as AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>,
                'removeOption',
                { option: removed }
              );
            }
            if (!multiple && renderValue !== undefined && !readOnly && inputValue === '') {
              handleValue(
                event,
                null as unknown as AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>,
                'removeOption',
                { option: value as unknown as Value }
              );
            }
            break;
          case 'Delete':
            if (
              multiple &&
              !readOnly &&
              inputValue === '' &&
              (value as unknown as Array<Value>).length > 0 &&
              focusedItem !== -1
            ) {
              const valArr = (value as unknown as Array<Value>).slice();
              const removed = valArr[focusedItem] as Value;
              valArr.splice(focusedItem, 1);
              handleValue(
                event,
                valArr as unknown as AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>,
                'removeOption',
                { option: removed }
              );
            }
            if (!multiple && renderValue !== undefined && !readOnly && inputValue === '') {
              handleValue(
                event,
                null as unknown as AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>,
                'removeOption',
                { option: value as unknown as Value }
              );
            }
            break;
          default:
        }
      }
    };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setFocused(true);

    if (focusedItem !== -1) {
      setFocusedItem(-1);
      focusItem(-1);
    }

    if (windowLostFocus.current) {
      windowLostFocus.current = false;
      return;
    }

    if (openOnFocus && !ignoreFocus.current) {
      handleOpen(event);
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (defaultIsActiveElementInListbox(listboxRef)) {
      inputRef.current?.focus();
      return;
    }

    setFocused(false);
    firstFocus.current = true;
    ignoreFocus.current = false;

    if (
      autoSelect &&
      highlightedIndexRef.current !== -1 &&
      popupOpen &&
      highlightReasonRef.current !== 'mouse' &&
      highlightReasonRef.current !== 'touch'
    ) {
      selectNewValue(event, filteredOptions[highlightedIndexRef.current] as Value, 'blur');
    } else if (autoSelect && freeSolo && inputValue !== '') {
      selectNewValue(event, inputValue, 'blur', 'freeSolo');
    } else if (clearOnBlur) {
      resetInputValue(event, value, 'blur');
    }

    handleClose(event, 'blur');
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    const valueChanged = inputValue !== newValue;

    if (valueChanged) {
      setInputValueState(newValue);
      touchScrolledRef.current = false;
      if (onInputChange) {
        onInputChange(event, newValue, 'input');
      }
    }

    if (newValue === '') {
      if (!disableClearable && !multiple && renderValue === undefined) {
        handleValue(
          event,
          null as unknown as AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>,
          'clear'
        );
      }
    } else {
      handleOpen(event);
    }

    if (valueChanged) {
      setInputPristine(false);
    }
  };

  const handleOptionMouseMove = (event: React.MouseEvent<HTMLLIElement>) => {
    const index = Number(event.currentTarget.getAttribute('data-option-index'));
    if (highlightedIndexRef.current !== index) {
      setHighlightedIndex({ event, index, reason: 'mouse' });
    } else {
      highlightReasonRef.current = 'mouse';
    }
    if (!isTouchRef.current) {
      touchScrolledRef.current = false;
    }
  };

  const handleOptionTouchStart = (event: React.TouchEvent<HTMLLIElement>) => {
    touchScrolledRef.current = false;
    setHighlightedIndex({
      event,
      index: Number(event.currentTarget.getAttribute('data-option-index')),
      reason: 'touch',
    });
    isTouchRef.current = true;
  };

  const handleOptionClick = (event: React.MouseEvent<HTMLLIElement>) => {
    const index = Number(event.currentTarget.getAttribute('data-option-index'));
    const option = filteredOptions[index] as Value;
    if (getOptionDisabled?.(option)) {
      return;
    }
    selectNewValue(event, option, 'selectOption');
    isTouchRef.current = false;
  };

  const handleItemDelete = (index: number) => (event: React.SyntheticEvent) => {
    const valArr = (value as unknown as Array<Value>).slice();
    const removed = valArr[index] as Value;
    valArr.splice(index, 1);
    handleValue(
      event,
      valArr as unknown as AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>,
      'removeOption',
      { option: removed }
    );
  };

  const handleSingleItemDelete = (event: React.SyntheticEvent) => {
    handleValue(
      event,
      null as unknown as AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>,
      'removeOption',
      { option: value as unknown as Value }
    );
  };

  const handlePopupIndicator = (event: React.SyntheticEvent) => {
    if (open) {
      handleClose(event, 'toggleInput');
    } else {
      handleOpen(event);
    }
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!contains(event.currentTarget, event.target as Node)) {
      return;
    }
    if (anchorEl && !contains(anchorEl, event.target as Node)) {
      return;
    }
    if ((event.target as HTMLElement).getAttribute('id') !== id) {
      event.preventDefault();
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!contains(event.currentTarget, event.target as Node)) {
      return;
    }
    if (anchorEl && !contains(anchorEl, event.target as Node)) {
      return;
    }
    inputRef.current?.focus();

    if (
      selectOnFocus &&
      firstFocus.current &&
      inputRef.current &&
      (inputRef.current.selectionEnd ?? 0) - (inputRef.current.selectionStart ?? 0) === 0
    ) {
      inputRef.current.select();
    }
    firstFocus.current = false;
  };

  const handleInputMouseDown = (event: React.MouseEvent<HTMLInputElement>) => {
    if (
      !disabledProp &&
      (inputValue === '' || !open) &&
      event.button === 0
    ) {
      handlePopupIndicator(event);
    }
  };

  let dirty = freeSolo && inputValue.length > 0;
  dirty = dirty || (multiple ? (value as unknown as Array<Value>).length > 0 : value !== null);

  let groupedOptions: Array<Value> | Array<AutocompleteGroupedOption<Value>> = filteredOptions as Array<Value>;
  if (groupBy) {
    const indexBy = new Map<string, true>();
    groupedOptions = (filteredOptions as Array<Value>).reduce<Array<AutocompleteGroupedOption<Value>>>(
      (acc, option, index) => {
        const group = groupBy(option);
        const lastGroup = acc[acc.length - 1];
        if (lastGroup !== undefined && lastGroup.group === group) {
          lastGroup.options.push(option);
        } else {
          indexBy.set(group, true);
          acc.push({ key: index, index, group, options: [option] });
        }
        return acc;
      },
      []
    );
  }

  if (disabledProp && focused) {
    handleBlur({} as React.FocusEvent<HTMLInputElement>);
  }

  const setAnchorEl: (node: HTMLElement | null) => void = (node) => {
    setAnchorElState(node);
  };

  const result: UseAutocompleteReturnValue<Value, Multiple, DisableClearable, FreeSolo, boolean> = {
    getRootProps: (other = {}) => ({
      ...(other as AutocompleteRootProps),
      onKeyDown: handleKeyDown(other),
      onMouseDown: handleMouseDown,
      onClick: handleClick,
    }),
    getInputLabelProps: () => ({
      id: `${id}-label`,
      htmlFor: id,
    }),
    getInputProps: () => ({
      id,
      value: inputValue,
      onBlur: handleBlur,
      onFocus: handleFocus,
      onChange: handleInputChange,
      onMouseDown: handleInputMouseDown,
      'aria-activedescendant': popupOpen ? '' : undefined,
      'aria-autocomplete': autoComplete ? 'both' : 'list',
      'aria-controls': popupOpen ? `${id}-listbox` : undefined,
      'aria-expanded': popupOpen,
      autoComplete: 'off',
      ref: inputRef,
      autoCapitalize: 'none',
      spellCheck: false,
      role: 'combobox',
      disabled: disabledProp,
    }),
    getClearProps: () => ({
      tabIndex: -1,
      type: 'button',
      onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
        ignoreFocus.current = true;
        handleClear(event);
      },
    }),
    getItemProps: (((args: { index?: number } = {}) => {
      const index = args.index ?? 0;
      const baseProps: AutocompleteItemProps = {
        'data-item-index': index,
        tabIndex: -1,
        onDelete: multiple ? handleItemDelete(index) : handleSingleItemDelete,
      };
      if (multiple) {
        return { ...baseProps, key: index } as AutocompleteItemProps & { key: number };
      }
      if (readOnly) {
        const { onDelete: _onDelete, ...rest } = baseProps;
        void _onDelete;
        return rest as AutocompleteItemProps;
      }
      return baseProps;
    }) as unknown) as AutocompleteGetItemProps<Multiple>,
    getPopupIndicatorProps: () => ({
      tabIndex: -1,
      type: 'button',
      onClick: handlePopupIndicator,
    }),
    getListboxProps: () => ({
      role: 'listbox',
      id: `${id}-listbox`,
      'aria-labelledby': `${id}-label`,
      'aria-multiselectable': multiple ? true : undefined,
      ref: handleListboxRef as unknown as React.Ref<HTMLUListElement>,
      onMouseDown: (event: React.MouseEvent<HTMLUListElement>) => {
        event.preventDefault();
      },
      onScroll: () => {
        if (isTouchRef.current) {
          touchScrolledRef.current = true;
        }
      },
    }),
    getOptionProps: ({ index, option }) => {
      const selected = isOptionSelected(option);
      const disabled = getOptionDisabled ? getOptionDisabled(option) : false;
      return {
        key: getOptionKey?.(option as Value | AutocompleteFreeSoloValueMapping<FreeSolo>) ?? getOptionLabel(option),
        tabIndex: -1,
        role: 'option',
        id: `${id}-option-${index}`,
        onMouseMove: handleOptionMouseMove,
        onClick: handleOptionClick,
        onTouchStart: handleOptionTouchStart,
        'data-option-index': index,
        'aria-disabled': disabled,
        'aria-selected': selected,
      };
    },
    id,
    inputValue,
    value,
    dirty,
    expanded: popupOpen && anchorEl != null,
    popupOpen,
    focused: focused || focusedItem !== -1,
    anchorEl,
    setAnchorEl,
    focusedItem,
    groupedOptions,
  };

  return result;
};

const defaultFilterOptions = ((
  options: Array<unknown>,
  state: { inputValue: string; getOptionLabel: (option: unknown) => string }
) => {
  const input = state.inputValue.toLowerCase();
  if (input === '') {
    return options;
  }
  return options.filter((option) => {
    const label = state.getOptionLabel(option).toLowerCase();
    return label.includes(input);
  });
}) as unknown as <Value>(
  options: Array<Value>,
  state: FilterOptionsState<Value>
) => Array<Value>;

export default useAutocomplete;
